// Redimensionne et recompresse une image côté navigateur avant upload de logo — l'API backend
// (/admin/upload-logo) refuse tout data URL dépassant 1 Mo, mais une image générée par IA ou une
// simple photo de téléphone dépasse très souvent cette limite en résolution/format d'origine (PNG en
// particulier compresse mal les dégradés). Sans ce redimensionnement, l'upload échoue silencieusement
// pour l'utilisateur qui ne comprend pas pourquoi "le logo ne s'affiche pas" — un logo n'a de toute
// façon jamais besoin d'être affiché au-delà de quelques centaines de pixels dans l'app.
const MAX_DIMENSION = 480;
const MAX_DATA_URL_LENGTH = 900000; // marge sous la limite serveur de 1 000 000 caractères

export const resizeImageForUpload = (file: File): Promise<{ base64: string; mimeType: string }> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => reject(new Error('Image invalide ou format non supporté'));
            img.onload = () => {
                let { width, height } = img;
                if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                    const scale = MAX_DIMENSION / Math.max(width, height);
                    width = Math.round(width * scale);
                    height = Math.round(height * scale);
                }
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) { reject(new Error("Redimensionnement impossible (canvas non supporté)")); return; }
                // Fond blanc : un logo est affiché en petit format opaque (avatar arrondi) dans l'app —
                // aplatir une éventuelle transparence évite un rendu noir/incohérent selon le thème, et
                // permet une compression JPEG bien plus efficace qu'un PNG pour ce type d'image.
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);

                let quality = 0.92;
                let dataUrl = canvas.toDataURL('image/jpeg', quality);
                while (dataUrl.length > MAX_DATA_URL_LENGTH && quality > 0.35) {
                    quality -= 0.12;
                    dataUrl = canvas.toDataURL('image/jpeg', quality);
                }
                if (dataUrl.length > MAX_DATA_URL_LENGTH) {
                    reject(new Error('Cette image reste trop volumineuse même après compression — essayez une image plus simple.'));
                    return;
                }
                resolve({ base64: dataUrl.split(',')[1], mimeType: 'image/jpeg' });
            };
            img.src = reader.result as string;
        };
        reader.readAsDataURL(file);
    });
};
